import { useState, useEffect, useCallback, useMemo } from "react";
import axiosInstance from "services/httpInterceptor";

const PAGE_SIZE = 15;
const ALL_COMPETITORS_ID = "all_competitors";

/**
 * usePostFeed
 *
 * Fetches posts for one or all competitors and provides client-side
 * category filtering + sort.
 *
 * When competitorId === ALL_COMPETITORS_ID, fetches all competitorIds
 * in parallel (Promise.all) and merges the results, tagging each post
 * with the companyId it came from so PostFeedCard can display the right
 * company badge.
 *
 * Sort modes:
 *   "latest"  — by date descending
 *   "engaged" — by (likes + comments + shares) descending
 */
const usePostFeed = (competitorId, platform, presetKey, allCompetitorIds = []) => {
  const [allPosts, setAllPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [sortMode, setSortMode] = useState("latest");
  const [displayCount, setDisplayCount] = useState(PAGE_SIZE);

  const isAllView = competitorId === ALL_COMPETITORS_ID;

  const fetchSingle = useCallback((cId) =>
    axiosInstance
      .get(`/v1/${platform}/posts/paged`, {
        params: {
          companyId: cId,
          preset: presetKey.toUpperCase(),
          page: 0,
          size: 50, // per-competitor limit — keeps total manageable
        },
      })
      .then((res) =>
        (res.data?.content || []).map((p) => ({ ...p, _companyId: cId }))
      )
      .catch(() => []),
  [platform, presetKey]);

  const fetchPosts = useCallback(async () => {
    if (!presetKey) return;
    if (isAllView && allCompetitorIds.length === 0) {
      setAllPosts([]);
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);
    setDisplayCount(PAGE_SIZE);

    try {
      if (isAllView) {
        // Parallel fetch for all competitors
        const results = await Promise.all(allCompetitorIds.map(fetchSingle));
        setAllPosts(results.flat());
      } else {
        if (!competitorId) return;
        const posts = await fetchSingle(competitorId);
        setAllPosts(posts);
      }
    } catch (err) {
      setError(err);
      setAllPosts([]);
    } finally {
      setLoading(false);
    }
  }, [competitorId, isAllView, allCompetitorIds, presetKey, fetchSingle]);

  useEffect(() => {
    fetchPosts();
  }, [fetchPosts]);

  // Reset display count when filter/sort changes
  useEffect(() => {
    setDisplayCount(PAGE_SIZE);
  }, [selectedCategory, sortMode]);

  // Distinct categories from data
  const allCategories = useMemo(() => {
    const cats = new Set();
    allPosts.forEach((p) => {
      if (p.analysis?.type) cats.add(p.analysis.type);
    });
    return Array.from(cats).sort();
  }, [allPosts]);

  // Filter
  const filtered = useMemo(() => {
    if (!selectedCategory) return allPosts;
    return allPosts.filter((p) => p.analysis?.type === selectedCategory);
  }, [allPosts, selectedCategory]);

  // Sort
  const sorted = useMemo(() => {
    if (sortMode === "engaged") {
      return [...filtered].sort((a, b) => {
        const ea = (a.likes || 0) + (a.comments || 0) + (a.shares || 0);
        const eb = (b.likes || 0) + (b.comments || 0) + (b.shares || 0);
        return eb - ea;
      });
    }
    return [...filtered].sort((a, b) => {
      if (!a.timestamp) return 1;
      if (!b.timestamp) return -1;
      return new Date(b.timestamp) - new Date(a.timestamp);
    });
  }, [filtered, sortMode]);

  const posts = sorted.slice(0, displayCount);
  const hasMore = displayCount < sorted.length;
  const loadMore = () => setDisplayCount((c) => c + PAGE_SIZE);

  return {
    posts,
    allCategories,
    loading,
    error,
    selectedCategory,
    setSelectedCategory,
    sortMode,
    setSortMode,
    hasMore,
    loadMore,
    total: sorted.length,
    isAllView,
  };
};

export { ALL_COMPETITORS_ID };
export default usePostFeed;
